class ProductCardCarousel extends HTMLElement {
  constructor() {
    super();
    this.images = [];
    this.prevBtn = null;
    this.nextBtn = null;
    this.index = 0;
    this.observer = null;
    this.mediaQuery = window.matchMedia('(pointer:fine)');
    this.handleMediaChange = this.handleMediaChange.bind(this);
    this.hideTimeout = null;
    this.track = null;
  }

  connectedCallback() {
    this.mediaQuery.addEventListener('change', this.handleMediaChange);

    if (this.mediaQuery.matches) {
      this.lazyInit();
    } else {
      this.initMobileScrollProgress();
    }
  }

  disconnectedCallback() {
    this.mediaQuery.removeEventListener('change', this.handleMediaChange);
  }

  handleMediaChange(e) {
    if (e.matches) {
      this.lazyInit();
    } else {
      this.teardownCarousel();
    }
  }

  lazyInit() {
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.initCarousel();
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );
    this.observer.observe(this);
  }

  initCarousel() {
    this.images = this.querySelectorAll('img');
    this.prevBtn = this.querySelector('.carousel-prev');
    this.nextBtn = this.querySelector('.carousel-next');

    if (!this.images.length || !this.prevBtn || !this.nextBtn) return;

    this.images.forEach((img, i) => {
      Object.assign(img.style, {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        transition: 'opacity 0.3s ease-in-out',
        opacity: i === 0 ? 1 : 0,
        zIndex: i === 0 ? 1 : 0,
      });
      img.setAttribute('aria-hidden', i !== 0);
    });

    const stopEvent = (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      e.stopPropagation();
    };

    [this.prevBtn, this.nextBtn].forEach((btn) => {
      btn.addEventListener('pointerdown', stopEvent);
      btn.addEventListener('click', (e) => {
        stopEvent(e);
        this.move(btn === this.prevBtn ? -1 : 1);
      });
    });

    this.setAttribute('tabindex', '0');
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this.move(-1);
      if (e.key === 'ArrowRight') this.move(1);
    });

    this.addEventListener('mouseenter', () => {
      this.makeImagesEager();
    });
  }

  teardownCarousel() {
    // Reset inline styles and remove event listeners
    this.images.forEach((img) => {
      img.removeAttribute('style');
      img.removeAttribute('aria-hidden');
    });

    [this.prevBtn, this.nextBtn].forEach((btn) => {
      if (!btn) return;
      btn.replaceWith(btn.cloneNode(true));
    });

    this.removeAttribute('tabindex');
  }

  move(delta) {
    this.makeImagesEager();

    this.index = (this.index + delta + this.images.length) % this.images.length;
    this.showImage(this.index);
  }

  makeImagesEager() {
    const allImages = this.querySelectorAll('img');
    allImages.forEach((img) => {
      img.classList.remove('!hidden');
      if (img.getAttribute('loading') === 'lazy') {
        img.setAttribute('loading', 'eager');
      }
    });
  }

  showImage(i) {
    this.images.forEach((img, j) => {
      img.style.opacity = j === i ? 1 : 0;
      img.style.zIndex = j === i ? 1 : 0;
      img.setAttribute('aria-hidden', j !== i);
    });
  }

  async initMobileScrollProgress() {
    this.images = this.querySelectorAll('img');
    this.images.forEach((img) => {
      img.removeAttribute('style');
      img.removeAttribute('aria-hidden');
    });

    const scroller = this.querySelector('.mobile-carousel');
    const bar = this.querySelector('.scroll-progress-bar');
    this.track = this.querySelector('.scroll-progress');

    if (!scroller || !bar || !this.track) return;

    const images = scroller.querySelectorAll('img');
    if (!images.length) return;

    const numImages = images.length;
    const MIN_MOVE = 10;

    let startX = 0;
    let startY = 0;
    let hasMoved = false;
    let isHorizontal = null; // null = unknown; true = horizontal; false = vertical

    /* --------------------------------------------
     * UPDATE PROGRESS BAR
     * -------------------------------------------- */
    const update = () => {
      const viewport = scroller.clientWidth;
      const trackWidth = this.track.clientWidth;
      const contentWidth = scroller.scrollWidth;

      if (!viewport || !trackWidth || !contentWidth) return;

      const maxScroll = contentWidth - viewport;
      const thumbWidth = viewport / numImages;

      bar.style.width = `${thumbWidth}px`;

      const scrollRatio = maxScroll > 0 ? scroller.scrollLeft / maxScroll : 0;
      const maxLeft = trackWidth - thumbWidth;

      bar.style.transform = `translateX(${scrollRatio * maxLeft}px)`;
    };

    /* --------------------------------------------
     * RESIZE / IMAGE LOAD SUPPORT
     * -------------------------------------------- */
    if (window.ResizeObserver) {
      const ro = new ResizeObserver(update);
      ro.observe(scroller);
    }

    const imageObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target;
            img.addEventListener('load', () => requestAnimationFrame(() => requestAnimationFrame(update)), {
              once: true,
            });
          }
        });
      },
      { root: scroller, rootMargin: '50px' }
    );

    images.forEach((img) => imageObserver.observe(img));

    const blockVertical = (e) => {
      if (e.cancelable) {
        e.preventDefault();
      }
    };

    scroller.addEventListener(
      'touchstart',
      (e) => {
        this.makeImagesEager();

        const t = e.touches[0];
        startX = t.clientX;
        startY = t.clientY;
        hasMoved = false;
        isHorizontal = null;

        scroller.removeEventListener('touchmove', blockVertical);
      },
      { passive: true }
    );

    scroller.addEventListener(
      'touchmove',
      (e) => {
        const t = e.touches[0];
        const dx = t.clientX - startX;
        const dy = t.clientY - startY;

        if (isHorizontal === null && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) {
          isHorizontal = Math.abs(dx) > Math.abs(dy);

          if (isHorizontal) {
            this.track.style.setProperty('opacity', '1');
            scroller.addEventListener('touchmove', blockVertical, { passive: false });
          }
        }

        if (isHorizontal && Math.abs(dx) > MIN_MOVE) {
          hasMoved = true;

          if (this.hideTimeout) {
            clearTimeout(this.hideTimeout);
          }
          this.hideTimeout = setTimeout(() => {
            this.track.style.setProperty('opacity', '0');
            this.hideTimeout = null;
          }, 1000);
        }
      },
      { passive: true }
    );

    this.addEventListener(
      'click',
      (e) => {
        if (hasMoved) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      },
      true
    );

    scroller.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);

    const initialUpdate = () => {
      update();
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(initialUpdate);
    });
  }

  waitForImages(container) {
    return new Promise((resolve) => {
      const imgs = container.querySelectorAll('img');
      if (!imgs.length) return resolve();

      let loaded = 0;
      imgs.forEach((img) => {
        if (img.complete) {
          loaded++;
          if (loaded === imgs.length) resolve();
        } else {
          img.addEventListener('load', () => {
            loaded++;
            if (loaded === imgs.length) resolve();
          });
          img.addEventListener('error', () => {
            loaded++;
            if (loaded === imgs.length) resolve();
          });
        }
      });
    });
  }
}

customElements.define('product-card-carousel', ProductCardCarousel);
